import Component from '../core/Component'
import enquire from 'enquire.js'
import { queries } from '../core/config'
import { toggleClass, queryAll } from '../utils/dom'
import Scroll from '../services/Scroll'

const STATES = {
    SUBNAV_ACTIVE: 'is-subnavActive',
    ACTIVE: 'is-active'
}

export default class Nav extends Component {

    constructor(element) {
        super(element)

        this.ref = {
            blocks: [],
            items: [],
            arrows: [],
            list: null,
            main: null
        }

        this.isMobile = false

        this.triangle = {
            a: {
                x: 0,
                y: 0
            },
            b: {
                x: 0,
                y: 0
            },
            c: {
                x: 0,
                y: 0
            }
        }

        this.enquireHandler = {
            match: this.attach,
            unmatch: this.detach
        }

        this.state = {
            current: null,
            pending: null,
            trigger: null,
            blockFocused: false
        }

        // debug
        // this.canvas = document.createElement('canvas')
        // this.canvas.setAttribute('style', 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 999')
        // this.canvas.width = window.innerWidth
        // this.canvas.height = window.innerHeight
        // document.body.appendChild(this.canvas)
        // this.context = this.canvas.getContext('2d')
    }

    prepare() {
        this.blocks = Object.fromEntries(this.ref.blocks.map(element => [element.dataset.id, element]))
        enquire.register(queries.largeUp, this.enquireHandler)
    }

    destroy() {
        enquire.unregister(queries.largeUp, this.enquireHandler)
    }

    attach = () => {
        this.observer = new MutationObserver(this.updateRefs)
        this.observer.observe(this.ref.main, {
            childList: true,
            subtree: true,
            attributes: false
        })

        this.updateRefs()

        this.ref.blocks.forEach(element => {
            element.addEventListener('mouseenter', this.handleBlockMouseEnter)
            element.addEventListener('mouseleave', this.handleBlockMouseLeave)
        })

        this.element.addEventListener('mouseenter', this.handleMouseEnter)
        this.element.addEventListener('mouseleave', this.handleMouseLeave)
        this.element.addEventListener('click', this.handleClick)
    }

    updateRefs = () => {
        this.ref.list = this.element.querySelector('[data-ref="list"]')
        this.ref.list.addEventListener('mousemove', this.handleListMouseMove)

        this.ref.items = queryAll('[data-ref="item"]', this.ref.list)
        this.ref.items.forEach(item => {
            item.addEventListener('mouseenter', this.handleItemMouseEnter)
            item.addEventListener('touchstart', this.handleItemTouchStart)
        })
    }

    detach = () => {
        this.ref.items.forEach(item => {
            item.removeEventListener('mouseenter', this.handleItemMouseEnter)
            item.removeEventListener('touchstart', this.handleItemTouchStart)
        })
        this.ref.blocks.forEach(element => {
            element.removeEventListener('mouseenter', this.handleBlockMouseEnter)
            element.removeEventListener('mouseleave', this.handleBlockMouseLeave)
            element.removeAttribute('style')
        })
        this.ref.list.removeEventListener('mousemove', this.handleListMouseMove)

        this.element.removeEventListener('mouseenter', this.handleMouseEnter)
        this.element.removeEventListener('mouseleave', this.handleMouseLeave)
        this.element.removeEventListener('click', this.handleClick)

        this.ref.list.removeEventListener('mousemove', this.handleMouseMove)

        this.close(this.state.current)

        this.observer?.disconnect()

        this.state = {
            current: null,
            pending: null,
            trigger: null,
            blockFocused: false
        }
    }

    handleClick = event => {
        const target = event.target.closest('a')
        if (target && !target.dataset.rel === 'services') {
            this.close(this.state.current)
        } else if (target && target.dataset.rel === 'services' && !this.isMobile) {
            event.preventDefault()
            event.stopPropagation()
        } else if (target) {
            this.close(this.state.current)
        }
    }

    handleMouseEnter = event => {
        this.ref.list.addEventListener('mousemove', this.handleMouseMove)
    }

    handleMouseLeave = event => {
        if (this.isMouseInTriangle(event)) {
            return
        }

        this.updateTriangle({
            a: {
                x: this.triangle.b.x,
                y: this.triangle.b.y
            }
        })

        this.ref.list.removeEventListener('mousemove', this.handleMouseMove)
        this.close(this.state.current)
    }

    handleBlockMouseEnter = event => {
        this.state.blockFocused = true
    }

    handleBlockMouseLeave = event => {
        this.state.blockFocused = false
    }

    handleItemMouseEnter = event => {
        this.state.pending = event.target.dataset.rel
        this.state.trigger = event.target
    }

    handleListMouseMove = event => {
        if (this.isMouseInTriangle(event)) {
            return
        }

        this.updateTriangle({
            a: {
                x: event.clientX,
                y: Math.max(event.clientY, 50)
            }
        })

        if (this.state.pending !== this.state.current) {
            this.show(this.state.pending)
        }
    }

    show(id) {
        if (id !== this.state.current) {
            this.close(this.state.current)
        }

        const block = this.blocks[id]
        if (block) {
            this.blockFocused = true
            if (block.dataset.type === 'float' && this.state.trigger) {
                const box = this.state.trigger.getBoundingClientRect()
                const elementBox = this.element.getBoundingClientRect()
                block.style.left = `${box.left + box.width/2 - elementBox.left}px`
            }

            if (block.dataset.type !== 'float' && this.state.trigger) {
                const blockBox = block.getBoundingClientRect()
                const triggerBox = this.state.trigger.getBoundingClientRect()
                const index = this.ref.blocks.indexOf(block)
                const arrow = this.ref.arrows[index]

                arrow.style.left = `${triggerBox.left + triggerBox.width/2 - blockBox.left}px`
            }

            block.classList.add(STATES.ACTIVE)
            const box = block.getBoundingClientRect()
            this.updateTriangle({
                b: {
                    x: box.left + box.width,
                    y: box.top + 10 // block overlap
                },
                c: {
                    x: box.left,
                    y: box.top + 10 // block overlap
                }
            })
        }

        this.state.current = id
    }

    close(id) {
        const block = this.blocks[id]

        if (block) {
            block.classList.remove(STATES.ACTIVE)
        }

        this.state.current = null
        this.state.blockFocused = false
    }

    isInTriangle(point) {
        const { a, b, c } = this.triangle
        const p = point

        const area = Math.abs(0.5 *(-b.y*c.x + a.y*(-b.x + c.x) + a.x*(b.y - c.y) + b.x*c.y))

        const s = 1/(2*area)*(a.y*c.x - a.x*c.y + (c.y - a.y)*p.x + (a.x - c.x)*p.y)
        const t = 1/(2*area)*(a.x*b.y - a.y*b.x + (a.y - b.y)*p.x + (b.x - a.x)*p.y)

        return s > 0 && t > 0 && 1-s-t > 0
    }

    isMouseInTriangle(event) {
        const point = {
            x: event.clientX,
            y: event.clientY
        }

        const inTriangle = this.isInTriangle(point)

        if (!inTriangle) {
            return (
                this.state.blockFocused &&
                point.y > this.triangle.b.y &&
                point.y > this.triangle.c.y
            )
        }

        return inTriangle
    }

    updateTriangle(points) {
        this.triangle = {
            ...this.triangle,
            ...points
        }

        // debug
        // const ctx = this.context
        // ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // ctx.beginPath()
        // ctx.moveTo(this.triangle.a.x, this.triangle.a.y)
        // ctx.lineTo(this.triangle.b.x, this.triangle.b.y)
        // ctx.lineTo(this.triangle.c.x, this.triangle.c.y)
        // ctx.lineTo(this.triangle.a.x, this.triangle.a.y)
        // ctx.stroke()
    }
}
